import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { deleteFormTemplate } from '../../graphql/resourceLibraryQueries';
import { CustomModal } from '../CustomModal';
import { Btn, BtnIconOnly, IconTrash, TEIQspinner } from '../ui';
import '../../css/ResourceLibraryContent.css';

interface FormTemplateItem {
    id: string;
    templateName: string;
    inspectionForm?: boolean | null;
    dailyServiceLogForm?: boolean | null;
    pmServiceScheduleForm?: boolean | null;
    createdAt?: string;
}

interface TemplatesListModalProps {
    isOpen: boolean;
    onClose: () => void;
    groupName: string;
    groupId: string;
    templates: FormTemplateItem[];
}

export const TemplatesListModal: React.FC<TemplatesListModalProps> = ({
    isOpen,
    onClose,
    groupName,
    templates: initialTemplates,
}) => {
    const [templates, setTemplates] = useState<FormTemplateItem[]>(initialTemplates);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [templateToDelete, setTemplateToDelete] = useState<FormTemplateItem | null>(null);
    const [confirmReady, setConfirmReady] = useState(false);

    useEffect(() => {
        if (!templateToDelete) {
            setConfirmReady(false);
            return;
        }
        setConfirmReady(false);
        const timer = window.setTimeout(() => setConfirmReady(true), 400);
        return () => window.clearTimeout(timer);
    }, [templateToDelete]);

    const getFormType = (template: FormTemplateItem): string => {
        if (template.dailyServiceLogForm) return 'Daily Service';
        if (template.inspectionForm) return 'Inspection';
        if (template.pmServiceScheduleForm) return 'PM Service';
        return 'Form';
    };

    const getFormTypeBadgeClass = (template: FormTemplateItem): string => {
        if (template.dailyServiceLogForm) return 'zrl-badge zrl-badge-draft';
        if (template.inspectionForm) return 'zrl-badge zrl-badge-standard';
        return 'zrl-badge zrl-badge-active';
    };

    const formatDate = (dateStr?: string): string => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const handleDelete = async (templateId: string) => {
        setDeletingId(templateId);
        try {
            await deleteFormTemplate(templateId);
            setTemplates((prev) => prev.filter((t) => t.id !== templateId));
        } catch (err) {
            console.error('Error deleting template:', err);
            Sentry.captureException(err);
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <>
        <CustomModal
            visible={isOpen}
            onDismiss={onClose}
            size="large"
            header={`Templates in ${groupName}`}
            bodyClassName="zrl-dialog-body"
            footer={
                <div className="modal-footer-actions">
                    <Btn variant="secondary-outlined" buttonText="Close" buttonOnClick={onClose} />
                </div>
            }
        >
                    {templates.length === 0 ? (
                        <div className="zrl-empty">No templates in this group.</div>
                    ) : (
                        <table className="zrl-table">
                            <thead>
                                <tr>
                                    <th>Template Name</th>
                                    <th>Type of Form</th>
                                    <th>Created</th>
                                    <th style={{ width: 50 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {templates.map((template) => (
                                    <tr key={template.id}>
                                        <td>
                                            <div className="zrl-table-name">{template.templateName}</div>
                                        </td>
                                        <td>
                                            <span className={getFormTypeBadgeClass(template)}>
                                                {getFormType(template)}
                                            </span>
                                        </td>
                                        <td>{formatDate(template.createdAt)}</td>
                                        <td>
                                            {deletingId === template.id ? (
                                                <TEIQspinner variant="sm" label={`Deleting ${template.templateName}`} />
                                            ) : (
                                                <BtnIconOnly
                                                    size="sm"
                                                    variant="danger"
                                                    icon={<IconTrash />}
                                                    ariaLabel={`Delete ${template.templateName}`}
                                                    onClick={() => setTemplateToDelete(template)}
                                                />
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
        </CustomModal>
        <CustomModal
            visible={!!templateToDelete}
            onDismiss={() => setTemplateToDelete(null)}
            header="Delete template?"
            bodyClassName="zrl-dialog-body"
            footer={
                <div className="modal-footer-actions">
                    <Btn
                        variant="secondary-outlined"
                        buttonText="Cancel"
                        buttonDisabled={!!deletingId}
                        buttonOnClick={() => setTemplateToDelete(null)}
                    />
                    <Btn
                        variant="danger"
                        buttonText={deletingId ? 'Deleting...' : 'Delete'}
                        buttonDisabled={!confirmReady || !!deletingId}
                        buttonOnClick={() => {
                            if (!templateToDelete) return;
                            const id = templateToDelete.id;
                            setTemplateToDelete(null);
                            handleDelete(id);
                        }}
                    />
                </div>
            }
        >
            <p>
                You are about to permanently delete <strong>{templateToDelete?.templateName}</strong> from this
                group. This cannot be undone.
            </p>
        </CustomModal>
        </>
    );
};
